import type { Session } from '../types/session';
import type { SessionTypeConfig } from '../types/event';

export interface AgendaEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  sessionTypeId: string;
  typeBackgroundColor: string;
  typeTextColor: string;
  typeName: string;
  /**
   * Lucide icon name for the session category. Renderers should look this
   * up against the curated icon map in `constants/sessionTypeIcons.ts` and
   * fall back to `Calendar` when null or unknown.
   */
  typeIconName: string | null;
  location: string | null;
  speakerNames: string[];
  description: string | null;
  isCompanion: boolean;
  isInternal: boolean;
}

/**
 * Converts a Session document to the calendar event shape expected by
 * react-big-calendar (admin) and react-native-big-calendar (mobile).
 */
export function sessionToAgendaEvent(
  session: Session,
  sessionTypes: SessionTypeConfig[],
): AgendaEvent {
  const sessionType = sessionTypes.find((st) => st.id === session.sessionTypeId);

  const toDate = (ts: unknown): Date => {
    if (ts && typeof ts === 'object' && 'toDate' in ts) {
      return (ts as { toDate: () => Date }).toDate();
    }
    return new Date(ts as string | number);
  };

  return {
    id: session.id,
    title: session.title,
    start: toDate(session.startTime),
    end: toDate(session.endTime),
    sessionTypeId: session.sessionTypeId,
    typeBackgroundColor: sessionType?.backgroundColor ?? '#9E9E9E',
    typeTextColor: sessionType?.textColor ?? '#FFFFFF',
    typeName: sessionType?.name ?? session.sessionTypeId,
    typeIconName: sessionType?.iconName ?? null,
    location: session.locationLabel,
    speakerNames: session.speakerNames,
    description: session.description,
    isCompanion: session.sessionTypeId === 'companion',
    isInternal: session.sessionTypeId === 'internal',
  };
}
